/**
 * UtmRedirectsDialog — список живых отложенных переходов UTM-источника:
 * кто открыл ссылку с целевой страницей, но ещё не был уведён на неё.
 * Строка удаляется на бэке (UtmRedirect), когда платформа подтверждает
 * фактический переход — поэтому здесь видны только «не перешедшие».
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ExternalLink, User, Route } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/providers/api-client';
import type { UtmSourceItem, UtmRedirectRecord } from './types';

interface UtmRedirectsDialogProps {
  source: UtmSourceItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 20;

const CHANNEL_LABELS: Record<UtmRedirectRecord['channel'], string> = {
  bot: 'Бот',
  miniapp: 'Mini App',
  web: 'Web',
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDisplayName(record: UtmRedirectRecord): string {
  if (record.user) {
    const fullName = [record.user.firstName, record.user.lastName].filter(Boolean).join(' ');
    if (fullName) {return fullName;}
    if (record.user.username) {return `@${record.user.username}`;}
    if (record.user.email) {return record.user.email;}
    return `User #${record.user.id}`;
  }
  if (record.telegramId) {return `Telegram ${record.telegramId}`;}
  if (record.visitorId) {return `Посетитель ${record.visitorId.slice(0, 8)}`;}
  return 'Аноним';
}

export function UtmRedirectsDialog({ source, open, onOpenChange }: UtmRedirectsDialogProps) {
  const [page, setPage] = useState(1);
  const sourceId = source?.id ?? null;
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, sourceId]);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['utm-sources', sourceId, 'redirects', page],
    queryFn: () => apiClient.get<UtmRedirectRecord[]>(
      `/tags/utm-sources/${sourceId}/redirects?page=${page}&limit=${PAGE_SIZE}`,
    ),
    enabled: open && sourceId !== null,
  });

  const total = source?.pendingRedirectCount ?? records.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  let content = (
    <div className="divide-y">
      {records.map((record) => {
        const userId = record.user?.id ?? null;
        const isClickable = userId !== null;
        return (
          <div
            key={record.id}
            className={`flex items-center gap-3 py-2.5 px-1 ${isClickable ? 'cursor-pointer hover:bg-muted/40 rounded transition-colors' : ''}`}
            onClick={() => { if (userId !== null) { navigate(`/users/show/${userId}`); onOpenChange(false); } }}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-1">
                {getDisplayName(record)}
                {isClickable && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                Цель: <code>{record.targetUrl}</code>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant="outline" className="text-xs">{CHANNEL_LABELS[record.channel] ?? record.channel}</Badge>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDate(record.createdAt)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    content = (
      <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
    );
  } else if (records.length === 0) {
    content = (
      <div className="flex items-center justify-center py-8 text-center text-muted-foreground">
        Нет незавершённых переходов — все открывшие ссылку были переведены на цель.
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Route className="h-5 w-5" />
            Не перешли: {source?.displayName ?? 'UTM-источник'}
            <Badge variant="secondary">{total}</Badge>
          </DialogTitle>
          <DialogDescription>
            Открыли ссылку, но ещё не были переведены на целевую страницу.
            Запись исчезает после фактического перехода.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {content}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
